import React, { useState } from 'react';
import { useAuth } from './AuthProvider';
import { Terminal, Shield, Zap } from 'lucide-react';
import { OAuthButtons } from './OAuthButtons';
import { User } from '../../types';

interface LoginFormProps {
  onSuccess?: (user: User) => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSuccess }): JSX.Element => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { login, register } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Trim all inputs
      const trimmedUsername = username.trim();
      const trimmedEmail = email.trim().toLowerCase();
      const trimmedPassword = password.trim();

      // Validate inputs
      if (!trimmedUsername || !trimmedPassword || (!isLogin && !trimmedEmail)) {
        throw new Error('Please fill in all fields');
      }

      if (trimmedPassword.length < 6) {
        throw new Error('Password must be at least 6 characters');
      }

      if (!isLogin) {
        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(trimmedEmail)) {
          throw new Error('Please enter a valid email address');
        }

        // Username validation
        if (trimmedUsername.length < 3) {
          throw new Error('Username must be at least 3 characters');
        }
        if (!/^[a-zA-Z0-9_-]+$/.test(trimmedUsername)) {
          throw new Error('Username can only contain letters, numbers, underscores, and hyphens');
        }
      }

      // Attempt login or registration
      if (isLogin) {
        const user = await login(trimmedUsername, trimmedPassword);
        if (onSuccess) onSuccess(user);
      } else {
        const success = await register(trimmedUsername, trimmedEmail, trimmedPassword);
        if (success && onSuccess) {
          // After registration, login returns the user
          const user = await login(trimmedUsername, trimmedPassword);
          onSuccess(user);
        }
      }
    } catch (err: any) {
      console.error('Form error:', err);
      if (err.message) {
        // Local validation errors
        setError(err.message);
      } else if (err.response?.data?.detail) {
        // Server errors
        const errorDetail = err.response.data.detail.toLowerCase();
        if (errorDetail.includes('email')) {
          setError('This email address is already registered. Please use a different email.');
        } else if (errorDetail.includes('username')) {
          setError('This username is already taken. Please choose a different username.');
        } else {
          setError(err.response.data.detail);
        }
      } else {
        setError('An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-md w-full mx-4 relative z-10">
        <div className="card p-8 shadow-2xl border-gray-600">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <Terminal className="w-12 h-12 text-blue-400" />
            </div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent mb-2">
              OpsQuest
            </h1>
            <h2 className="text-2xl font-bold text-white mb-2">
              {isLogin ? 'Welcome Back' : 'Get Started'}
            </h2>
            <p className="text-gray-400">
              {isLogin ? 'Access your DevOps challenges' : 'Start your DevOps journey'}
            </p>
          </div>

          {/* Features highlight for new users */}
          {!isLogin && (
            <div className="grid grid-cols-1 gap-4 mb-6">
              <div className="flex items-center space-x-3 p-3 bg-gray-700/30 rounded-lg">
                <Terminal className="w-5 h-5 text-green-400" />
                <span className="text-sm text-gray-300">Real Linux environments</span>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-gray-700/30 rounded-lg">
                <Shield className="w-5 h-5 text-blue-400" />
                <span className="text-sm text-gray-300">Safe isolated containers</span>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-gray-700/30 rounded-lg">
                <Zap className="w-5 h-5 text-purple-400" />
                <span className="text-sm text-gray-300">Progressive difficulty</span>
              </div>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="input"
                placeholder="Enter your username"
                required
                minLength={3}
                maxLength={50}
                pattern="[a-zA-Z0-9_-]+"
                title="Username can only contain letters, numbers, underscores, and hyphens"
              />
            </div>

            {!isLogin && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input"
                  placeholder="Enter your email"
                  required
                />
                <p className="text-xs text-gray-400 mt-1">You'll need this email to recover your account</p>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                placeholder="Enter your password"
                required
                minLength={6}
              />
              {!isLogin && (
                <p className="text-xs text-gray-400 mt-1">Minimum 6 characters</p>
              )}
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Processing...
                </span>
              ) : (
                isLogin ? 'Sign In' : 'Sign Up'
              )}
            </button>

            {/* OAuth separator and buttons */}
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-gray-900 text-gray-400">Or continue with</span>
                </div>
              </div>
              <div className="mt-6">
                <OAuthButtons onError={setError} />
              </div>
            </div>
          </form>

          <div className="text-center mt-6">
            <button
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
                setEmail('');  // Clear email when switching modes
              }}
              className="text-blue-400 hover:text-blue-300 text-sm transition-colors"
            >
              {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
            </button>
          </div>

          {/* Demo info */}
          <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <p className="text-blue-300 text-sm text-center">
              💡 New to DevOps? Start with the "Fix SSH Configuration" challenge!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

